import { Button } from '@/components/ui/button';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Component, useId, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

export class PageErrorBoundary extends Component<
  { children: ReactNode; descriptionId: string; onReset?: () => void; resetKey?: string; titleId: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: ReactNode;
    descriptionId: string;
    onReset?: () => void;
    resetKey?: string;
    titleId: string;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        aria-labelledby={this.props.titleId}
        aria-describedby={this.props.descriptionId}
        className="mx-auto flex min-h-64 max-w-2xl flex-col justify-center border border-destructive/40 bg-card p-6 text-card-foreground shadow-industrial"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0 space-y-2">
            <h2 id={this.props.titleId} className="text-lg font-semibold text-destructive">Page failed to load</h2>
            <p id={this.props.descriptionId} className="text-sm text-muted-foreground">
              Try again, or return home and reopen this area.
            </p>
            {this.state.error?.message ? (
              <p className="break-words text-xs text-muted-foreground">Error detail: {this.state.error.message}</p>
            ) : null}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button type="button" onClick={() => {
            this.setState({ hasError: false, error: null });
            this.props.onReset?.();
          }}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
          <Button asChild variant="outline">
            <Link to="/" onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onReset?.();
            }}>
              <Home className="mr-2 h-4 w-4" aria-hidden="true" />
              Go home
            </Link>
          </Button>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary() {
  const location = useLocation();
  const titleId = useId();
  const descriptionId = useId();

  return (
    <PageErrorBoundary descriptionId={descriptionId} resetKey={location.key} titleId={titleId}>
      <Outlet />
    </PageErrorBoundary>
  );
}
